import React, { useEffect, useMemo, useState } from 'react';
import {
    Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement,
    BarElement, Tooltip, Legend, Filler,
} from 'chart.js';
import { Line, Bar } from 'react-chartjs-2';
import {
    FaSync, FaUsers, FaChartLine, FaComments, FaBullhorn, FaPlay, FaFire,
    FaPlus, FaTimes, FaTrash, FaLayerGroup, FaGlobe,
} from 'react-icons/fa';
import { socialApi } from './api';
import {
    KIND, PLAT, RANGES, fmt, nssColor, movingAvg, addDaysStr,
    dateFromDays, acctHandle, kindOf, aggregate, TopPostCard, PostModal,
} from './analyticsShared';
import './generalDashboard.css';
import './groups.css';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, Tooltip, Legend, Filler);

const PALETTE = ['#073C5C', '#7c3aed', '#0ea5e9', '#F68D2E', '#16a34a', '#db2777', '#0891b2', '#ca8a04'];
const groupColor = (i) => PALETTE[i % PALETTE.length];
const METRICS = [
    { key: 'followers', label: 'Seguidores' },
    { key: 'er', label: 'Engagement' },
    { key: 'nss', label: 'NSS' },
    { key: 'posts', label: 'Publicaciones' },
    { key: 'views', label: 'Alcance video' },
];
const metricVal = (t, key) => (key === 'er' ? (t.er || 0) : key === 'nss' ? (t.nss || 0) : (t[key] || 0));

// Modal: publicaciones del grupo publicadas en la fecha/semana clickeada.
function GroupDayModal({ date, bucket, posts, onOpenPost, onClose }) {
    const end = addDaysStr(date, bucket === 'day' ? 1 : 7);
    const list = posts
        .filter((p) => { const d = (p.posted_at || '').slice(0, 10); return d >= date && d < end; })
        .sort((a, b) => (b.er ?? -1) - (a.er ?? -1));
    return (
        <div className="sa-modal-overlay" onClick={onClose}>
            <div className="gd-acct-modal" onClick={(e) => e.stopPropagation()}>
                <button className="sa-modal-close" onClick={onClose}><FaTimes /></button>
                <div className="gd-acct-head">
                    <span className="gd-acct-ic" style={{ color: '#F68D2E' }}><FaFire /></span>
                    <div className="gd-acct-id">
                        <h3>Publicaciones del {date}{bucket === 'week' ? ` al ${addDaysStr(date, 6)}` : ''}</h3>
                        <span className="gd-muted-h">{list.length} {list.length === 1 ? 'publicación' : 'publicaciones'}</span>
                    </div>
                </div>
                {list.length === 0 ? (
                    <p className="gd-note">No se publicaron posts del grupo en esta fecha. La línea de NSS refleja el sentimiento de los <b>comentarios recibidos</b> ese día, que pueden ser sobre publicaciones anteriores.</p>
                ) : (
                    <div className="gd-top-grid">
                        {list.map((p) => <TopPostCard key={`${p._accountId}-${p.id}`} post={p} onOpen={onOpenPost} />)}
                    </div>
                )}
            </div>
        </div>
    );
}

export default function AccountGroups({ username }) {
    const [accounts, setAccounts] = useState([]);
    const [overviews, setOverviews] = useState({});
    const [postsByAcct, setPostsByAcct] = useState({});
    const [groups, setGroups] = useState([]);
    const [selectedId, setSelectedId] = useState(null);
    const [rangeDays, setRangeDays] = useState(90);
    const [metric, setMetric] = useState('followers');
    const [flowBucket, setFlowBucket] = useState('day');
    const [smooth, setSmooth] = useState(true);
    const [flowDay, setFlowDay] = useState(null);
    const [modalPost, setModalPost] = useState(null);
    const [streaming, setStreaming] = useState(false);
    const [err, setErr] = useState('');
    const storeKey = `sa_groups_${username || 'anon'}`;

    // Updater funcional + persistencia (evita perder cambios bajo auto-batching).
    const persist = (updater) => setGroups((prev) => {
        const next = typeof updater === 'function' ? updater(prev) : updater;
        try { localStorage.setItem(storeKey, JSON.stringify(next)); } catch (_) { /* cuota */ }
        return next;
    });

    // Cuentas + grupos guardados (o semilla por kind la primera vez).
    useEffect(() => {
        if (!username) return undefined;
        let cancelled = false;
        setErr('');
        socialApi.listAccounts(username)
            .then((r) => {
                if (cancelled) return;
                const accs = r.accounts || [];
                setAccounts(accs);
                let saved = null;
                try { saved = JSON.parse(localStorage.getItem(storeKey) || 'null'); } catch (_) { saved = null; }
                if (Array.isArray(saved) && saved.length) {
                    setGroups(saved);
                } else {
                    const own = accs.filter((a) => kindOf(a) === 'own').map((a) => a.id);
                    const interest = accs.filter((a) => kindOf(a) === 'interest').map((a) => a.id);
                    const seed = [];
                    if (own.length) seed.push({ id: 1, name: 'Mis marcas', accountIds: own });
                    if (interest.length) seed.push({ id: 2, name: 'Competencia', accountIds: interest });
                    setGroups(seed);
                    try { localStorage.setItem(storeKey, JSON.stringify(seed)); } catch (_) { /* cuota */ }
                }
            })
            .catch((e) => { if (!cancelled) setErr(e.message || 'No se pudo cargar la información'); });
        return () => { cancelled = true; };
    }, [username, storeKey]);

    // Overviews por cuenta (bucket = flowBucket, para el flujo de sentimiento).
    useEffect(() => {
        setOverviews({});
        if (!username || accounts.length === 0) { setStreaming(false); return undefined; }
        let cancelled = false;
        const from = dateFromDays(rangeDays);
        setStreaming(true);
        let settled = 0;
        const total = accounts.length;
        accounts.forEach((a) => {
            socialApi.overview(username, a.id, from, '', flowBucket)
                .then((r) => { if (!cancelled && r?.overview) setOverviews((prev) => ({ ...prev, [a.id]: r.overview })); })
                .catch(() => {})
                .finally(() => { if (cancelled) return; settled += 1; if (settled >= total) setStreaming(false); });
        });
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [accounts, rangeDays, flowBucket]);

    // Posts por cuenta (para las publicaciones combinadas del grupo).
    useEffect(() => {
        setPostsByAcct({});
        if (!username || accounts.length === 0) return undefined;
        let cancelled = false;
        const from = dateFromDays(rangeDays);
        accounts.forEach((a) => {
            socialApi.posts(username, a.id, from, '')
                .then((r) => { if (!cancelled && r?.posts) setPostsByAcct((prev) => ({ ...prev, [a.id]: r.posts })); })
                .catch(() => {});
        });
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [accounts, rangeDays]);

    // Gestión de grupos.
    const addGroup = () => persist((prev) => {
        const id = prev.reduce((m, g) => Math.max(m, g.id), 0) + 1;
        return [...prev, { id, name: `Grupo ${prev.length + 1}`, accountIds: [] }];
    });
    const renameGroup = (id, name) => persist((prev) => prev.map((g) => (g.id === id ? { ...g, name } : g)));
    const deleteGroup = (id) => persist((prev) => prev.filter((g) => g.id !== id));
    const toggleAccount = (id, accId) => persist((prev) => prev.map((g) => {
        if (g.id !== id) return g;
        const has = g.accountIds.includes(accId);
        return { ...g, accountIds: has ? g.accountIds.filter((x) => x !== accId) : [...g.accountIds, accId] };
    }));

    const byId = useMemo(() => Object.fromEntries(accounts.map((a) => [a.id, a])), [accounts]);
    const computed = useMemo(() => groups.map((g, i) => {
        const accs = g.accountIds.map((id) => byId[id]).filter(Boolean);
        return { ...g, color: groupColor(i), accounts: accs, agg: aggregate(accs, overviews) };
    }), [groups, byId, overviews]);

    const selected = computed.find((g) => g.id === selectedId) || computed[0] || null;

    // Publicaciones combinadas del grupo seleccionado.
    const groupPosts = useMemo(() => {
        if (!selected) return [];
        const ids = new Set(selected.accountIds);
        const out = [];
        accounts.forEach((a) => {
            if (!ids.has(a.id)) return;
            (postsByAcct[a.id] || []).forEach((p) => out.push({
                ...p, _handle: a.username, _platform: a.platform, _kind: kindOf(a), _track_by: a.track_by, _accountId: a.id,
            }));
        });
        return out;
    }, [selected, accounts, postsByAcct]);
    const bestPosts = useMemo(() => [...groupPosts].sort((a, b) => (b.er ?? -1) - (a.er ?? -1)).slice(0, 8), [groupPosts]);

    // Flujo de sentimiento combinado del grupo.
    const evo = selected?.agg.evolution || [];
    const nssLine = smooth ? movingAvg(evo.map((b) => b.nss), 3) : evo.map((b) => b.nss);
    const hasSent = (selected?.agg.totals.analyzed || 0) > 0 && evo.length > 0;

    const totals = selected?.agg.totals;
    const kpis = totals ? [
        { lbl: 'Seguidores', val: fmt(totals.followers), ic: <FaUsers /> },
        { lbl: 'Engagement', val: totals.er != null ? `${totals.er}%` : '—', ic: <FaChartLine /> },
        { lbl: 'NSS', val: totals.nss != null ? totals.nss : '—', ic: <FaComments />, color: totals.nss != null ? nssColor(totals.nss) : undefined },
        { lbl: 'Publicaciones', val: fmt(totals.posts), ic: <FaBullhorn /> },
        { lbl: 'Alcance video', val: totals.views ? fmt(totals.views) : '—', ic: <FaPlay /> },
    ] : [];

    const hasAccounts = accounts.length > 0;

    return (
        <div className="gd-root">
            <div className="gd-head">
                <div>
                    <h2 className="gd-title"><FaLayerGroup /> Grupos</h2>
                    <p className="gd-subtitle">Agrupa varios ítems y mira su <b>desempeño conjunto</b> (métricas, sentimiento y publicaciones combinadas).</p>
                </div>
                <div className="gd-head-actions">
                    <select value={rangeDays} onChange={(e) => setRangeDays(Number(e.target.value))} style={{ cursor: 'pointer' }}>
                        {RANGES.map((r) => <option key={r.days} value={r.days}>{r.label}</option>)}
                    </select>
                </div>
            </div>

            {err && <div className="gd-card" style={{ borderColor: '#fecaca', background: '#fef2f2', color: '#b91c1c', marginBottom: 16 }}>{err}</div>}
            {!username && <div className="gd-card">Inicia sesión para usar los grupos.</div>}
            {username && !hasAccounts && <div className="gd-card"><h4>Aún no hay cuentas</h4><p className="gd-subtitle" style={{ margin: 0 }}>Agrega cuentas en <b>Redes Sociales</b> para agruparlas.</p></div>}

            {username && hasAccounts && (
                <>
                    {streaming && <div className="gd-streaming"><FaSync className="spin" /> Cargando métricas… {Object.keys(overviews).length}/{accounts.length} cuentas</div>}

                    {/* Barra de agrupaciones (mismo estilo que el carrusel de Redes Sociales) */}
                    <div className="sa-topbar grp-topbar">
                        <button className="sa-add-chip" onClick={addGroup}><FaPlus /> Nueva agrupación</button>
                        <div className="sa-account-carousel">
                            {computed.length === 0 && <span className="sa-chip-empty">Crea tu primera agrupación →</span>}
                            {computed.map((g) => (
                                <button key={g.id} type="button"
                                    className={`sa-chip ${selected && selected.id === g.id ? 'active' : ''}`}
                                    style={{ '--kind-color': g.color }} onClick={() => setSelectedId(g.id)}>
                                    <span className="sa-chip-dot" />
                                    <span className="sa-chip-handle">{g.name}</span>
                                    <span className="grp-tab-count">{g.accounts.length}</span>
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Desempeño conjunto del grupo seleccionado */}
                    {selected && (
                        <div className="gd-card grp-detail" style={{ borderTopColor: selected.color }}>
                            <div className="grp-detail-head">
                                <span className="grp-dot" style={{ background: selected.color }} />
                                <input className="grp-name-input" value={selected.name}
                                    onChange={(e) => renameGroup(selected.id, e.target.value)} placeholder="Nombre de la agrupación" />
                                <button className="grp-del" onClick={() => deleteGroup(selected.id)} title="Eliminar agrupación"><FaTrash /></button>
                            </div>
                            <div className="grp-detail-items">
                                {selected.accounts.length === 0 && <span className="gd-subtitle">Sin ítems — agrégalos →</span>}
                                {selected.accounts.map((a) => {
                                    const meta = PLAT[a.platform] || {};
                                    const Icon = meta.Icon || FaGlobe;
                                    return (
                                        <span className="grp-chip" key={a.id}>
                                            <Icon style={{ color: meta.color }} /> {acctHandle(a)}
                                            <button onClick={() => toggleAccount(selected.id, a.id)} title="Quitar"><FaTimes /></button>
                                        </span>
                                    );
                                })}
                                {accounts.filter((a) => !selected.accountIds.includes(a.id)).length > 0 && (
                                    <select className="grp-add-select" value=""
                                        onChange={(e) => { if (e.target.value) toggleAccount(selected.id, Number(e.target.value)); }}>
                                        <option value="">+ Agregar ítem…</option>
                                        {accounts.filter((a) => !selected.accountIds.includes(a.id)).map((a) => (
                                            <option key={a.id} value={a.id}>{acctHandle(a)} · {KIND[kindOf(a)].label}</option>
                                        ))}
                                    </select>
                                )}
                            </div>

                            {/* KPIs combinados */}
                            <div className="gd-acct-kpis">
                                {kpis.map((m) => (
                                    <div className="gd-acct-kpi" key={m.lbl}>
                                        <span className="gd-acct-kpi-ic">{m.ic}</span>
                                        <span className="gd-acct-kpi-val" style={{ color: m.color }}>{m.val}</span>
                                        <span className="gd-acct-kpi-lbl">{m.lbl}</span>
                                    </div>
                                ))}
                            </div>

                            {/* Sentimiento combinado en el tiempo */}
                            <div className="grp-detail-chart-head">
                                <h5>Sentimiento conjunto (NSS)</h5>
                                <div className="gd-flow-controls">
                                    <div className="gd-seg">
                                        <button className={flowBucket === 'week' ? 'active' : ''} onClick={() => setFlowBucket('week')}>Semana</button>
                                        <button className={flowBucket === 'day' ? 'active' : ''} onClick={() => setFlowBucket('day')}>Día</button>
                                    </div>
                                    <button className={`gd-smooth ${smooth ? 'active' : ''}`} onClick={() => setSmooth((s) => !s)}><FaChartLine /> Suavizar</button>
                                </div>
                            </div>
                            <div className="gd-chart">
                                {!hasSent ? (
                                    <div className="gd-empty-guard"><FaComments /><p>Sin comentarios analizados en los ítems de este grupo. Analiza posts en <b>Redes Sociales</b> para poblar la tendencia.</p></div>
                                ) : (
                                    <Line
                                        data={{
                                            labels: evo.map((b) => (b.bucket || '').slice(0, 10)),
                                            datasets: [{
                                                label: 'NSS', data: nssLine, borderColor: selected.color,
                                                backgroundColor: 'rgba(7,60,92,.10)', fill: true, tension: smooth ? 0.45 : 0.3,
                                                pointRadius: 2, borderWidth: 2, spanGaps: true,
                                            }],
                                        }}
                                        options={{
                                            responsive: true, maintainAspectRatio: false,
                                            onClick: (evt, els) => { if (els.length) setFlowDay((evo[els[0].index].bucket || '').slice(0, 10)); },
                                            onHover: (evt, els) => { const t = evt?.native?.target; if (t) t.style.cursor = els.length ? 'pointer' : 'default'; },
                                            plugins: { legend: { display: false } },
                                            scales: { y: { min: -100, max: 100, grid: { color: '#f1f5f9' } }, x: { grid: { display: false } } },
                                        }}
                                    />
                                )}
                            </div>
                            {hasSent && <p className="gd-note">Haz click en un punto para ver las publicaciones del grupo de esa fecha.</p>}

                            {/* Publicaciones combinadas del grupo */}
                            <h5><FaFire style={{ color: '#F68D2E' }} /> Mejores publicaciones del grupo</h5>
                            {bestPosts.length === 0 ? (
                                <p className="gd-subtitle">{selected.accounts.length === 0 ? 'Agrega ítems al grupo.' : 'Sin publicaciones en el rango.'}</p>
                            ) : (
                                <div className="gd-top-grid">
                                    {bestPosts.map((p) => <TopPostCard key={`${p._accountId}-${p.id}`} post={p} onOpen={setModalPost} />)}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Comparación entre grupos (secundario) */}
                    {computed.length > 1 && (
                        <div className="gd-card">
                            <div className="gd-card-head">
                                <h4>Comparación entre grupos</h4>
                                <div className="gd-seg">
                                    {METRICS.map((m) => (
                                        <button key={m.key} className={metric === m.key ? 'active' : ''} onClick={() => setMetric(m.key)}>{m.label}</button>
                                    ))}
                                </div>
                            </div>
                            <div className="gd-chart">
                                <Bar
                                    data={{
                                        labels: computed.map((g) => g.name),
                                        datasets: [{
                                            label: METRICS.find((m) => m.key === metric)?.label,
                                            data: computed.map((g) => metricVal(g.agg.totals, metric)),
                                            backgroundColor: computed.map((g) => g.color),
                                            borderRadius: 6,
                                        }],
                                    }}
                                    options={{
                                        responsive: true, maintainAspectRatio: false,
                                        plugins: { legend: { display: false } },
                                        scales: { y: { beginAtZero: true, grid: { color: '#f1f5f9' } }, x: { grid: { display: false } } },
                                    }}
                                />
                            </div>
                        </div>
                    )}

                    <p className="gd-note" style={{ textAlign: 'center', marginTop: 4 }}>
                        Las agrupaciones se guardan en este navegador (privadas). Más adelante se podrán compartir a nivel organización.
                    </p>
                </>
            )}

            {flowDay && selected && (
                <GroupDayModal
                    date={flowDay} bucket={flowBucket} posts={groupPosts}
                    onOpenPost={(p) => { setFlowDay(null); setModalPost(p); }}
                    onClose={() => setFlowDay(null)}
                />
            )}
            {modalPost && <PostModal username={username} post={modalPost} onClose={() => setModalPost(null)} />}
        </div>
    );
}
